import type { PlayerInfo } from '@am/protocol';
import type { BuildingTypeId, BuildingView, RejectReason } from '@am/sim';
import { create } from 'zustand';
import { type Account, currentAccount } from './account';
import type { ConnectionCheck } from './build/connections';
import type { PaywallReason } from './full-version';
import type { Lang, MessageKey } from './i18n';
import type { RadioLine } from './radio';
import { type GraphicsSettings, normaliseGraphics, presetSettings } from './render/graphics';
import type { SpectatorView } from './render/spectator';
import { relayUrl } from './server';
import type { OpsView } from './sim/worker-protocol';
import type { Post } from './social';
import { DEFAULT_SOUND_MIX, type SoundMix } from './sound-mix';
import type { DelayAlert } from './ui/delay-alert';

/** An Air Force flypast on its way (sim flyover.ts), as its notice tells it. */
export interface FlypastNotice {
  /** The runways it closes, for how long (game minutes), and the ministry's compensation. */
  runwayIds: readonly number[];
  minutes: number;
  compensation: number;
  /** When the notice came (ticks). */
  time: number;
}

export type Tool =
  | 'select'
  | 'land'
  | 'build'
  | 'road'
  | 'service'
  | 'taxiway'
  | 'taxiLine'
  | 'bulldoze'
  | 'junction'
  | 'toll';

export type Screen = 'menu' | 'game';

/** A contract paper on screen (see Paper.tsx). */
export type Paper =
  | { kind: 'sign'; id: number }
  | { kind: 'terminate'; id: number }
  /** A contract's record: how the airline sees the airport, and why (see Paper.tsx). */
  | { kind: 'report'; id: number }
  | { kind: 'suspended'; contractId: number; airline: number; reason: 'cancellations' | 'score' };

/** A line of co-op chat. */
export interface ChatLine {
  id: number;
  playerId: number;
  name: string;
  text: string;
  /** performance.now() when it arrived (lines fade out after a while). */
  at: number;
}

/** Chat lines kept on screen. */
export const CHAT_LINES = 30;

const MUTED_KEY = 'am27.mutedPlayers';

function readMuted(): string[] {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(MUTED_KEY) ?? '[]');
    return Array.isArray(list) ? list.filter((n): n is string => typeof n === 'string') : [];
  } catch {
    return [];
  }
}

/** Hides (or shows again) a co-op player's chat lines, on this device from now on. */
export function setMuted(name: string, muted: boolean): void {
  const list = useGame.getState().mutedPlayers.filter((n) => n !== name);
  if (muted) list.push(name);
  useGame.setState({ mutedPlayers: list });
  try {
    localStorage.setItem(MUTED_KEY, JSON.stringify(list));
  } catch {
    // Private mode: muted for this visit only.
  }
}

/** One finished day, for the history charts. */
export interface DaySummary {
  day: number;
  income: number;
  costs: number;
  departures: number;
  passengers: number;
  cancelled: number;
  rating: number;
  /** Departures on time, percent (absent in histories saved before it was kept). */
  onTime?: number;
}

/** Days kept for the charts. */
export const HISTORY_DAYS = 30;

/** A saved game to start from (see saves.ts). */
export interface SaveStart {
  id: string;
  name: string;
  data: Uint8Array;
  history?: DaySummary[];
}

/**
 * How a game starts. `fresh`: a new game (not a saved one opened again): the captain welcomes the
 * player and offers the tutorial on its first entry (ui/Lesson.tsx).
 */
export type StartMode =
  | { kind: 'offline'; save?: SaveStart; airportName?: string; fresh?: boolean }
  | {
      kind: 'host';
      relayUrl: string;
      save?: SaveStart;
      airportName?: string;
      fresh?: boolean;
      /** List the room on everyone's co-op page (see community.ts). */
      listed?: boolean;
    }
  | { kind: 'join'; relayUrl: string; roomCode: string };

/** Ghost preview result shown next to the cursor. */
/** A page of the about and legal window (LegalWindow.tsx). */
export type LegalTab = 'about' | 'privacy' | 'terms' | 'licences';

/** A sheet of the phone's interface (PhoneHud.tsx). */
export type PhoneSheet = 'airport' | 'goals' | 'menu';

export interface HoverPlan {
  ok: boolean;
  cost: number;
  reason: RejectReason | null;
  /** What the building being placed would connect to (build/connections.ts). */
  checks?: readonly ConnectionCheck[];
  screenX: number;
  screenY: number;
}

export interface Toast {
  id: number;
  kind: 'info' | 'error';
  key: MessageKey;
  params?: Record<string, string | number>;
  /**
   * Where on the map it happens (tiles): clicking the notification takes the camera there (and,
   * with a heading and tilt, turns it to look that way).
   */
  at?: { x: number; z: number; yaw?: number; pitch?: number };
  /** A window to open on a click (where to read more and act). */
  open?: { opsTab?: GameState['opsTab']; mgmtTab?: GameState['mgmtTab'] };
}

/** A weather photo mode can show instead of the game's own. */
export type PhotoWeather = 'clear' | 'rain' | 'storm' | 'snow' | 'winter' | 'dust';

export interface GameState {
  /** The signed-in player (see account.ts), or null on the sign-in screen. */
  account: Account | null;
  // Preferences (persisted)
  lang: Lang;
  playerName: string;
  relayUrl: string;
  graphics: GraphicsSettings;
  /** Sound volume 0–1. */
  volume: number;
  /** How loud each kind of sound is, on top of the volume (Settings → Sound). */
  soundMix: SoundMix;
  /** Interface and controls (Settings → Game). */
  controls: Controls;

  // Navigation
  screen: Screen;
  startMode: StartMode | null;
  menuError: { key: MessageKey; params?: Record<string, string | number> } | null;

  // Session
  loaded: boolean;
  /** The save dialog is open. */
  saveDialog: boolean;
  /** Touch screens: a placement is waiting for the player's confirmation. */
  touchConfirm: boolean;
  /**
   * The player plays with a finger (or a pen): the last press on the map was one, or, before
   * any, the screen is a touch screen. Placements then wait for a tick (TouchConfirm).
   */
  touch: boolean;
  /** Touch: the building catalogue folded away to show the map while a building is placed. */
  catalogFolded: boolean;
  /** Touch, standing in for Shift: the taxiway tool paves an area, the taxi line tool erases. */
  drawAlt: boolean;
  /** The phone's sheet open down the right side, or null. */
  phoneSheet: PhoneSheet | null;
  /** The about and legal window's page, or null (closed); on the title screen or in a game. */
  legal: LegalTab | null;
  /** Contract papers waiting to be read or signed, first on screen. */
  papers: Paper[];
  /** Info layer: noise contours round the runways. */
  noiseMap: boolean;
  /** The last calls on the tower frequency (radio.ts), oldest first. */
  radio: RadioLine[];
  /**
   * The spectator camera (render/spectator.ts): watching aircraft instead of the map; null: off.
   * `id` is the aircraft chosen (null: the tour picks).
   */
  spectate: { view: SpectatorView; tour: boolean; id: number | null } | null;
  /** What the spectator camera shows, for its caption (published a few times a second). */
  watched: { id: number; flightId: number; cls: number; airline: number; phase: number } | null;
  /** The aircraft whose flight card is open (clicked on), until it has flown off; null: none. */
  flightCard: number | null;
  /** A delay out of all proportion being shown (the game stopped for it); null: none. */
  delayAlert: DelayAlert | null;
  /** The speed the game ran at when it stopped for the alert (to carry on at). */
  delayResumeSpeed: number;
  /** The Air Force's flypast notice being shown (the game stopped for it); null: none. */
  flypast: FlypastNotice | null;
  /** The speed the game ran at when it stopped for the notice (carried on at after the show). */
  flypastResumeSpeed: number;
  /**
   * Photo mode: no interface, the light of a chosen hour and a chosen weather (null: the game's
   * own; the weather only as shown, the game's own goes on).
   */
  photoMode: boolean;
  photoHour: number | null;
  photoWeather: PhotoWeather | null;
  /** The how-to-play card (F1). */
  helpOpen: boolean;
  /** The Academy (manual) is open (F1): `helpOpen`; the article to show, if one was asked for. */
  academyArticle: string | null;
  /** The settings window is open over the game. */
  settingsOpen: boolean;
  /** The lesson being followed (ui/Lesson.tsx) and its step, or null. */
  lesson: { id: string; step: number } | null;
  /** A game year just ended: its report is shown (year index from 0), or null. */
  yearReport: number | null;
  /** Co-op chat, oldest first, and whether the chat input is open. */
  chat: ChatLine[];
  chatOpen: boolean;
  /** Co-op players whose chat lines are hidden (by name; kept on this device, see Chat.tsx). */
  mutedPlayers: string[];
  /** Finished days, oldest first (kept with saved games, not part of the simulation). */
  history: DaySummary[];
  /** The social feed (newest first), posts not yet seen, and the open social panel tab. */
  posts: Post[];
  socialUnread: number;
  socialTab: 'feed' | 'ratings' | null;
  /** Loading progress: connecting, world received, graphics starting, shaders, ready. */
  loadStage: LoadStage;
  /** Picture of the title screen's airport, the backdrop of the loading screen. */
  loadingImage: string | null;
  playerId: number;
  roomCode: string | null;
  online: boolean;
  players: readonly PlayerInfo[];
  authorityId: number;
  speed: number;
  syncing: boolean;
  latencyMs: number | null;

  // Simulation mirror
  money: number;
  time: number;
  buildings: readonly BuildingView[];
  ops: OpsView | null;
  /** Open tab of the airline operations panel, or null when closed. */
  opsTab: 'offers' | 'contracts' | 'flights' | 'apron' | 'album' | null;
  /** Open tab of the management window (staff and finance), or null when closed. */
  mgmtTab: 'staff' | 'finance' | 'airlines' | 'marketing' | null;

  // Building
  tool: Tool;
  /** Building type being placed with the build tool. */
  placing: BuildingTypeId | null;
  /** A building being moved (the build tool then places it again elsewhere), or null. */
  moving: number | null;
  /** Quarter turns for the next placement (R). */
  rotation: number;
  /** Building shown in the info panel. */
  selectedId: number | null;
  /** The airport directorate whose report book is open (see ReportBook.tsx), or null. */
  reportBook: number | null;
  /** The full version is owned (or there is none to buy: see full-version.ts). */
  fullVersion: boolean;
  /** Its price in the player's currency, as Google Play gives it (null: not known yet). */
  fullPrice: string | null;
  /** The full version's page is open, and why (what the player reached for). */
  paywall: PaywallReason | null;
  /** A tile of the road junction shown in the junction panel (junction tool), or null. */
  junctionAt: { x: number; y: number } | null;
  hover: HoverPlan | null;

  toasts: Toast[];
  /** A place the camera is asked to go to (from a notification); `n` tells requests apart. */
  lookAt: { x: number; z: number; n: number; yaw?: number; pitch?: number } | null;
  /** Account achievements just unlocked, shown one after another (AchievementSplash). */
  achievementQueue: string[];
  showDebug: boolean;
  /** Catalog pictures per building type (data URLs), kept across games. */
  thumbnails: Record<number, string>;
}

const PREFS_KEY = 'am27.prefs';

/** Name ideas for a new airport (the player can type anything). */
export const AIRPORT_NAMES = [
  'Toros Havalimanı',
  'Kapadokya Havalimanı',
  'Ege Havalimanı',
  'Karadeniz Havalimanı',
  'Anadolu Havalimanı',
  'Yeşilırmak Havalimanı',
  'Fırat Havalimanı',
  'Uludağ Havalimanı',
  'Erciyes Havalimanı',
  'Marmara Havalimanı',
  'Kızılırmak Havalimanı',
  'Ağrı Dağı Havalimanı',
];

export function randomAirportName(not?: string): string {
  const options = AIRPORT_NAMES.filter((n) => n !== not);
  return options[Math.floor(Math.random() * options.length)] ?? 'Anadolu Havalimanı';
}

/** Co-op rooms run on the game server (Vite forwards /ws in development; see server.ts). */
const DEFAULT_RELAY = relayUrl();

/** How the game handles: camera, saving, the frame-rate meter. */
export interface Controls {
  /** Frame-rate meter on screen: none, the rate, or the rate with renderer details. */
  fpsMeter: 'off' | 'fps' | 'full';
  /** Camera pan speed (multiplier). */
  panSpeed: number;
  /** Pan when the mouse touches the edge of the screen. */
  edgePan: boolean;
  /** Wheel down zooms in instead of out. */
  invertZoom: boolean;
  /** Minutes between autosaves (0: never). */
  autosaveMinutes: 0 | 2 | 5 | 10;
  /** Only notifications that matter (warnings, achievements); routine ones are left out. */
  quietToasts: boolean;
  /** The tower frequency (radio.ts): off, or written in a log. */
  radio: 'off' | 'text';
  /** Crash and stall reports sent to the developers (diagnostics.ts). */
  reports: boolean;
  /** Stop the game and say why when a delay is out of all proportion (see DelayAlert). */
  delayAlerts: boolean;
  /** Causes (hold reasons) of such delays the player asked not to be told about again. */
  mutedDelays: number[];
}

export const DEFAULT_CONTROLS: Controls = {
  fpsMeter: 'off',
  panSpeed: 1,
  edgePan: false,
  invertZoom: false,
  autosaveMinutes: 5,
  quietToasts: false,
  radio: 'text',
  reports: true,
  delayAlerts: true,
  mutedDelays: [],
};

interface Prefs {
  lang: Lang;
  playerName: string;
  relayUrl: string;
  graphics: GraphicsSettings;
  /** Sound volume 0–1. */
  volume: number;
  soundMix: SoundMix;
  controls: Controls;
}

/** The languages the game speaks (i18n.ts LANGS; not imported from there, which imports this). */
const SPOKEN: readonly Lang[] = ['tr', 'en', 'ru', 'fr', 'de'];

/** The browser's language if the game speaks it, else English. */
function browserLang(): Lang {
  const code = navigator.language.slice(0, 2).toLowerCase() as Lang;
  return SPOKEN.includes(code) ? code : 'en';
}

function loadPrefs(): Prefs {
  const defaults: Prefs = {
    lang: browserLang(),
    playerName: '',
    relayUrl: DEFAULT_RELAY,
    volume: 0.7,
    soundMix: DEFAULT_SOUND_MIX,
    // Phones and small tablets start on the light preset; the player can raise it.
    graphics: presetSettings(
      navigator.maxTouchPoints > 0 && Math.min(screen.width, screen.height) < 820 ? 'low' : 'high',
    ),
    controls: DEFAULT_CONTROLS,
  };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    const saved = raw ? (JSON.parse(raw) as Partial<Prefs>) : {};
    const prefs: Prefs = {
      ...defaults,
      ...saved,
      // Older prefs kept a preset name only, and no controls.
      graphics: saved.graphics ? normaliseGraphics(saved.graphics) : defaults.graphics,
      controls: { ...DEFAULT_CONTROLS, ...(saved.controls ?? {}) },
      // Older prefs had one volume only.
      soundMix: { ...DEFAULT_SOUND_MIX, ...(saved.soundMix ?? {}) },
    };
    if (!SPOKEN.includes(prefs.lang)) prefs.lang = defaults.lang;
    // The radio is no longer spoken: a voice setting from before reads as written.
    if ((prefs.controls.radio as string) === 'voice') prefs.controls.radio = 'text';
    // Co-op used to run on its own port; it now shares the game server's address.
    if (prefs.relayUrl === `ws://${location.hostname || 'localhost'}:8787`) {
      prefs.relayUrl = DEFAULT_RELAY;
    }
    return prefs;
  } catch {
    return defaults;
  }
}

export function savePrefs(
  state: Pick<
    GameState,
    'lang' | 'playerName' | 'relayUrl' | 'graphics' | 'volume' | 'soundMix' | 'controls'
  >,
): void {
  try {
    const prefs: Prefs = {
      lang: state.lang,
      playerName: state.playerName,
      relayUrl: state.relayUrl,
      graphics: state.graphics,
      volume: state.volume,
      soundMix: state.soundMix,
      controls: state.controls,
    };
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage can be unavailable (private mode); preferences are a convenience only.
  }
}

export const LoadStage = { Connecting: 0, World: 1, Graphics: 2, Shaders: 3, Ready: 4 } as const;
export type LoadStage = (typeof LoadStage)[keyof typeof LoadStage];

export const sessionDefaults = {
  loaded: false,
  loadStage: LoadStage.Connecting as LoadStage,
  saveDialog: false,
  touchConfirm: false,
  catalogFolded: false,
  drawAlt: false,
  phoneSheet: null,
  papers: [],
  chat: [],
  chatOpen: false,
  mutedPlayers: readMuted(),
  noiseMap: false,
  radio: [],
  spectate: null,
  watched: null,
  flightCard: null,
  delayAlert: null,
  delayResumeSpeed: 0,
  flypast: null,
  flypastResumeSpeed: 0,
  photoMode: false,
  photoHour: null,
  photoWeather: null,
  helpOpen: false,
  academyArticle: null,
  settingsOpen: false,
  lesson: null,
  yearReport: null,
  history: [],
  posts: [],
  socialUnread: 0,
  socialTab: null,
  playerId: 0,
  roomCode: null,
  online: false,
  players: [],
  authorityId: 0,
  speed: 1,
  syncing: false,
  latencyMs: null,
  money: 0,
  time: 0,
  buildings: [],
  ops: null,
  opsTab: null,
  mgmtTab: null,
  tool: 'select',
  placing: null,
  moving: null,
  rotation: 0,
  selectedId: null,
  reportBook: null,
  fullVersion: false,
  fullPrice: null,
  paywall: null,
  junctionAt: null,
  hover: null,
  toasts: [],
  lookAt: null,
  achievementQueue: [],
} satisfies Partial<GameState>;

export const useGame = create<GameState>()(() => ({
  account: currentAccount(),
  ...loadPrefs(),
  screen: 'menu',
  startMode: null,
  menuError: null,
  showDebug: false,
  touch: typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
  legal: null,
  thumbnails: {},
  loadingImage: null,
  ...sessionDefaults,
}));

/**
 * The right-hand side shows what was asked for last: picking a building (or a junction) closes
 * an open flights, management or social window over it, and opening one of those shows it.
 */
useGame.subscribe((s, prev) => {
  const picked =
    (s.selectedId !== null && s.selectedId !== prev.selectedId) ||
    (s.junctionAt !== null && s.junctionAt !== prev.junctionAt);
  if (picked && (s.opsTab !== null || s.mgmtTab !== null || s.socialTab !== null)) {
    useGame.setState({ opsTab: null, mgmtTab: null, socialTab: null });
  }
  // A window opened, a building picked or a tool taken: the phone's sheet gives way.
  const shown =
    picked ||
    (s.opsTab !== null && s.opsTab !== prev.opsTab) ||
    (s.mgmtTab !== null && s.mgmtTab !== prev.mgmtTab) ||
    (s.socialTab !== null && s.socialTab !== prev.socialTab) ||
    (s.tool !== prev.tool && s.tool !== 'select') ||
    (s.helpOpen && !prev.helpOpen);
  if (shown && s.phoneSheet !== null) useGame.setState({ phoneSheet: null });
});

let toastId = 0;

export function pushToast(
  kind: Toast['kind'],
  key: MessageKey,
  params?: Toast['params'],
  at?: Toast['at'],
  open?: Toast['open'],
): void {
  if (kind === 'info' && useGame.getState().controls.quietToasts) return;
  const toast: Toast = { id: ++toastId, kind, key, params, at, open };
  useGame.setState((s) => ({ toasts: [...s.toasts.slice(-4), toast] }));
  // One with a place to go and see stays a little longer, to be clicked.
  window.setTimeout(
    () => {
      useGame.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== toast.id) }));
    },
    at || open ? 8000 : 4000,
  );
}
